import { Expose, Exclude, Transform, Type } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument, anyToDate } from './Models'

export class KpiCounts {
  @Expose()
  public analysed: number = 0
  @Expose()
  public obsolete: number = 0
  @Expose()
  public updated: number = 0
}

export class MajorChangeBullet {
  @Expose()
  public designation: string = ''
  @Expose()
  public summary: string = ''
}

export interface ReportGroupCountsV3 {
  deadline_passed: number
  text_updated: number
  for_information: number
  no_change: number
  identification: number
}

export interface ReportFinalisationV3 {
  status: 'open' | 'finalised' | 'unknown'
  finalised_at: Date | null
  finalised_by: string | null
  signatory_name: string | null
}

function parseGroupCounts(raw: unknown): ReportGroupCountsV3 | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const n = (k: string) => (typeof r[k] === 'number' ? (r[k] as number) : 0)
  return {
    deadline_passed: n('deadline_passed'),
    text_updated: n('text_updated'),
    for_information: n('for_information'),
    no_change: n('no_change'),
    identification: n('identification'),
  }
}

@Exclude()
export class RegulationReport extends _FirebaseDocument {
  @Expose()
  public reference_date: string = ''

  @Expose()
  public regulations_count: number = 0

  @Expose()
  @Transform(anyToDate)
  public generated_at: Date | null = null

  @Expose()
  @Type(() => KpiCounts)
  public kpi: KpiCounts = new KpiCounts()

  // MYAC-904 change 3 ("if the tldr failed, just don't show it") — explicit
  // `@Transform` (same reason as `identifying`/`awaiting_curation` below):
  // class-transformer overwrites the `= []` default with `undefined` when
  // the source doc omits the key entirely (or the summary agent wrote
  // something other than an array). `ReportTldr.vue` / the PDF generators
  // already render nothing for an EMPTY array (`v-if="bullets.length"` /
  // `if (tldrBullets.length === 0) return`) — this guarantees they always
  // get a real array to call `.length` on, so a failed/missing TL;DR
  // degrades to "no section", never a render crash.
  @Expose()
  @Transform(({ obj, key }) => (Array.isArray(obj[key]) ? obj[key] : []))
  public tldr_bullets: string[] = []

  @Expose()
  @Type(() => MajorChangeBullet)
  public major_changes: MajorChangeBullet[] = []

  // Designations from the input list that could not be analysed (validation or
  // LLM categorization failure). Empty for reports generated before this field
  // existed. Surfaced so the report is transparent about partial results.
  // LEGACY-ONLY field — the v2 pipeline never writes it (see `identifying` /
  // `awaiting_curation` below); kept exactly as-is so old reports keep
  // rendering through the legacy branch.
  @Expose()
  public not_analysed: string[] = []

  // v2 pipeline marker (MYAC-904, work/2026-07-29-regmon-v2-report/plan.md
  // Phase 1/4): writers set "v2" so the report view can branch ONCE — "v2" ->
  // new renderer, absent/anything else -> legacy renderer. Additive/tolerant:
  // absent on every report generated before v2.
  @Expose()
  public schema_version: string | null = null

  // v2-only split of `not_analysed` by WHY a row has no entry yet: no
  // resolved family at all (`identifying`) vs. a family that exists but is
  // still `unverified` (`awaiting_curation`). Both stay empty for legacy
  // reports (spec invariant #3 — every row appears, never dropped).
  // Explicit `@Transform`: class-transformer overwrites the `= []` default
  // with `undefined` when the source plain object omits the key entirely —
  // this guarantees `.length` callers (the not-analysed transparency box)
  // never see `undefined` even on a malformed/partial doc.
  @Expose()
  @Transform(({ obj, key }) => (Array.isArray(obj[key]) ? obj[key] : []))
  public identifying: string[] = []

  @Expose()
  @Transform(({ obj, key }) => (Array.isArray(obj[key]) ? obj[key] : []))
  public awaiting_curation: string[] = []

  // MYAC-723 — render-convenience pointer to this run's surfaced
  // standards-advisory suggestions (the durable state lives on the
  // standards_advisory board, not here). Additive/tolerant: absent on every
  // report written before this field existed. Same explicit `@Transform` as
  // `identifying`/`awaiting_curation` above and the same reason: without it,
  // class-transformer overwrites the `= []` default with `undefined` when the
  // source doc omits the key entirely (every legacy report), and a caller
  // reading `.length` on that would crash instead of seeing "none".
  @Expose()
  @Transform(({ obj, key }) => (Array.isArray(obj[key]) ? obj[key] : []))
  public suggested_designations: string[] = []

  // ── v3 (MYAC-1496 lot 1) ── present only when `schema_version === 'v3'`;
  // the cards live in `cards/`. Counters are in LINES.
  @Expose()
  @Transform(anyToDate)
  public period_start: Date | null = null

  @Expose()
  @Transform(anyToDate)
  public period_end: Date | null = null

  /** Where `period_start` comes from: `first_report`, `last_report` or
   *  `chosen_date` (read through `periodBasis`). Absent before 02/10/2026. */
  @Expose()
  public period_basis: string | null = null

  @Expose()
  @Transform(({ obj, key }) => (typeof obj[key] === 'number' ? obj[key] : null))
  public lines_total: number | null = null

  @Expose()
  @Transform(({ obj, key }) => parseGroupCounts(obj[key]))
  public group_counts: ReportGroupCountsV3 | null = null

  @Expose()
  @Transform(({ obj, key }) => (typeof obj[key] === 'number' ? obj[key] : null))
  public lines_to_review: number | null = null

  @Expose()
  @Transform(({ obj, key }) => (typeof obj[key] === 'number' ? obj[key] : null))
  public lines_remaining: number | null = null

  @Expose()
  @Transform(({ obj, key }) => {
    const raw = obj[key] as Record<string, unknown> | undefined
    if (!raw || typeof raw !== 'object') return null
    return {
      status: raw.status === 'finalised' ? 'finalised' : raw.status === 'open' ? 'open' : 'unknown',
      finalised_at: anyToDate({ obj: raw, key: 'finalised_at' }) ?? null,
      finalised_by: typeof raw.finalised_by === 'string' ? raw.finalised_by : null,
      signatory_name: typeof raw.signatory_name === 'string' ? raw.signatory_name : null,
    }
  })
  public finalisation: ReportFinalisationV3 | null = null

  collection(): string {
    return 'regulation_reports'
  }

  static fromFirebase(o: DocumentSnapshot): RegulationReport | null {
    return _FirebaseDocument._fromFirebase(RegulationReport, o)
  }
}
